import { Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { useState } from 'react'

import { useExtensionsI18n } from '../i18n/provider'

export interface PickListItem {
  id: number
  label: string
}

interface PickListProps {
  allItems: PickListItem[]
  assignedIds: number[]
  onAssignedChange: (ids: number[]) => void
  assignedLabels?: Record<number, string>
  itemNoun?: string
  maxAssigned?: number
  disabled?: boolean
}

export function PickList({
  allItems,
  assignedIds,
  onAssignedChange,
  assignedLabels = {},
  itemNoun = 'items',
  maxAssigned,
  disabled = false,
}: PickListProps) {
  const { text } = useExtensionsI18n()
  const [search, setSearch] = useState('')
  const assignedSet = new Set(assignedIds)

  const assignedItems = assignedIds.map((id) => {
    const fromAll = allItems.find((item) => item.id === id)
    return {
      id,
      label: fromAll?.label ?? assignedLabels[id] ?? `#${id}`,
    }
  })

  const availableItems = allItems
    .filter((item) => !assignedSet.has(item.id))
    .filter(
      (item) =>
        !search.trim() ||
        item.label.toLowerCase().includes(search.trim().toLowerCase()),
    )

  const atLimit = maxAssigned != null && assignedIds.length >= maxAssigned

  const handleAdd = (itemId: number) => {
    if (assignedSet.has(itemId) || atLimit) return
    onAssignedChange([...assignedIds, itemId])
  }

  const handleRemove = (itemId: number) => {
    onAssignedChange(assignedIds.filter((id) => id !== itemId))
  }

  const assignedHeader = maxAssigned
    ? text(
        'extensions.pickList.assignedWithLimit',
        'Assigned {{itemNoun}}: {{count}} / {{max}}',
        { itemNoun, count: assignedItems.length, max: maxAssigned },
      )
    : text('extensions.pickList.assigned', 'Assigned {{itemNoun}}: {{count}}', {
        itemNoun,
        count: assignedItems.length,
      })

  return (
    <HStack align="start" gap={4}>
      <Box flex="1" borderWidth="1px" borderRadius="md" overflow="hidden">
        <Box px={3} py={2} bg="bg.muted" fontWeight="medium" fontSize="sm">
          {assignedHeader}
        </Box>
        <VStack
          align="stretch"
          gap={0}
          p={2}
          minH="120px"
          maxH="320px"
          overflowY="auto"
        >
          {assignedItems.length === 0 ? (
            <Box p={3} textAlign="center">
              <Text color="fg.muted" fontSize="sm">
                {text(
                  'extensions.pickList.noneAssigned',
                  'No {{itemNoun}} added yet.',
                  { itemNoun },
                )}
              </Text>
            </Box>
          ) : (
            assignedItems.map((item) => (
              <Box
                key={item.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={3}
                py={1.5}
                _hover={{ bg: 'bg.muted' }}
                borderRadius="sm"
              >
                <Text fontSize="sm">{item.label}</Text>
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="error"
                  onClick={() => handleRemove(item.id)}
                  disabled={disabled}
                >
                  {text('extensions.pickList.remove', 'Remove')}
                </Button>
              </Box>
            ))
          )}
        </VStack>
      </Box>

      <Box flex="1" borderWidth="1px" borderRadius="md" overflow="hidden">
        <Box px={3} py={2} bg="bg.muted" fontWeight="medium" fontSize="sm">
          {text(
            'extensions.pickList.available',
            'Available {{itemNoun}}: {{count}}',
            {
              itemNoun,
              count: availableItems.length,
            },
          )}
        </Box>
        <Box p={2}>
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={text(
              'extensions.pickList.searchAvailable',
              'Search available {{itemNoun}}',
              { itemNoun },
            )}
            disabled={disabled}
            mb={2}
          />
          <VStack
            align="stretch"
            gap={0}
            minH="80px"
            maxH="320px"
            overflowY="auto"
          >
            {availableItems.length === 0 ? (
              <Box p={3} textAlign="center">
                <Text color="fg.muted" fontSize="sm">
                  {atLimit
                    ? text(
                        'extensions.pickList.maxReached',
                        'Maximum of {{max}} {{itemNoun}} reached.',
                        { max: maxAssigned ?? 0, itemNoun },
                      )
                    : text(
                        'extensions.pickList.noneAvailable',
                        'No {{itemNoun}} available.',
                        { itemNoun },
                      )}
                </Text>
              </Box>
            ) : (
              availableItems.map((item) => (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={3}
                  py={1.5}
                  _hover={{ bg: 'bg.muted' }}
                  borderRadius="sm"
                >
                  <Text fontSize="sm">{item.label}</Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => handleAdd(item.id)}
                    disabled={disabled || atLimit}
                  >
                    {text('extensions.common.add', 'Add')}
                  </Button>
                </Box>
              ))
            )}
          </VStack>
        </Box>
      </Box>
    </HStack>
  )
}
